// hooks/useTutorialProgress.js
import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'tutorial_progress'

export function useTutorialProgress(tutorialId) {
  const [progress, setProgress] = useState({
    currentStep: 0,
    completed: false,
    lastVisited: null
  })

  // Load progress from localStorage on mount
  useEffect(() => {
    if (!tutorialId) return

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const allProgress = JSON.parse(stored)
        const tutorialProgress = allProgress[tutorialId]
        if (tutorialProgress) {
          setProgress(tutorialProgress)
        }
      }
    } catch (error) {
      console.error('Error loading tutorial progress:', error)
    }
  }, [tutorialId])

  // Save progress to localStorage
  const saveProgress = useCallback((stepIndex, totalSteps) => {
    if (!tutorialId) return

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const allProgress = stored ? JSON.parse(stored) : {}
      
      const isCompleted = stepIndex >= totalSteps - 1
      const newProgress = {
        currentStep: stepIndex,
        completed: isCompleted,
        lastVisited: new Date().toISOString()
      }

      allProgress[tutorialId] = newProgress
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress))
      setProgress(newProgress)
    } catch (error) {
      console.error('Error saving tutorial progress:', error)
    }
  }, [tutorialId])

  // Mark tutorial as completed
  const markCompleted = useCallback(() => {
    if (!tutorialId) return

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const allProgress = stored ? JSON.parse(stored) : {}
      
      setProgress(prev => {
        const updatedProgress = {
          ...prev,
          completed: true,
          lastVisited: new Date().toISOString()
        }
        
        allProgress[tutorialId] = updatedProgress
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress))
        return updatedProgress
      })
    } catch (error) {
      console.error('Error marking tutorial as completed:', error)
    }
  }, [tutorialId])

  // Get completion status for all tutorials
  const getAllProgress = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.error('Error getting all progress:', error)
      return {}
    }
  }, [])

  return {
    progress,
    saveProgress,
    markCompleted,
    isCompleted: progress.completed,
    getAllProgress
  }
}

